import React from "react";
import { Link } from "react-router-dom";
import { useStore } from "../lib/store";
import { formatINR } from "../lib/api";
import { Trash2, Minus, Plus, ShoppingBag, X } from "lucide-react";
import MagicCheckoutButton from "./MagicCheckout";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal, coupon, setCoupon, settings } = useStore();

  const freeAbove = settings?.shipping?.free_above || 1299;
  const remaining = Math.max(freeAbove - subtotal, 0);
  const pct = Math.min((subtotal / freeAbove) * 100, 100);

  return (
    <div className={`fixed inset-0 z-[70] ${cartOpen ? "" : "pointer-events-none"}`} aria-hidden={!cartOpen}>
      <div className={`absolute inset-0 bg-forest/40 transition-opacity duration-500 ${cartOpen ? "opacity-100" : "opacity-0"}`}
        onClick={() => setCartOpen(false)} />
      <aside role="dialog" aria-label="Shopping bag" data-testid="cart-drawer" data-state={cartOpen ? "open" : "closed"}
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-[transform,visibility] duration-500 ease-[cubic-bezier(.3,1,.3,1)] ${cartOpen ? "translate-x-0 visible" : "translate-x-full invisible"}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h2 className="font-display text-2xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Your bag {cart.length ? `(${cart.reduce((s, l) => s + l.qty, 0)})` : ""}
          </h2>
          <button data-testid="cart-drawer-close" onClick={() => setCartOpen(false)} aria-label="Close bag"
            className="p-2 rounded-full hover:bg-surface transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-surface flex items-center justify-center">
              <ShoppingBag className="w-8 h-8" />
            </div>
            <p className="font-display text-lg font-medium">Your bag is empty</p>
            <p className="text-sm text-charcoal/70">Fill it with bottles, bento boxes and little joys.</p>
            <Link to="/collections/shop-all" data-testid="cart-empty-shop-link" onClick={() => setCartOpen(false)}
              className="bg-forest text-white hover:bg-forest-dark px-6 py-3 rounded-full transition-colors">Start shopping</Link>
          </div>
        ) : (
          <>
            {settings?.shipping?.free_above ? (
              <div className="px-5 pt-4">
                <div className="bg-surface rounded-2xl p-3">
                  <p className="text-xs font-semibold mb-2">
                    {remaining > 0 ? <>Add {formatINR(remaining)} more for <span className="text-[#0D8756]">free shipping</span></> : <span className="text-[#0D8756]">You've unlocked free shipping</span>}
                    <span className="block font-normal text-charcoal/50 mt-0.5">Prepaid orders above {formatINR(freeAbove)}</span>
                  </p>
                  <div className="h-1.5 rounded-full bg-line overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
                    <div className="h-full bg-[#0D8756] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            ) : null}

            <ul className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="cart-drawer-items">
              {cart.map((l) => (
                <li key={l.variant_id} className="flex gap-3 border-b border-line pb-3">
                  <img src={l.image} alt={l.image_alt} width="64" height="64" className="w-20 h-20 rounded-xl object-cover shrink-0 bg-surface" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold leading-snug truncate">{l.name}</p>
                    <p className="text-xs text-charcoal/60 mt-0.5">{l.variant_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 border border-line rounded-full px-1">
                        <button data-testid={`cart-qty-minus-${l.variant_id}`} onClick={() => updateQty(l.variant_id, l.qty - 1)} aria-label={`Decrease quantity of ${l.name}`} className="p-1.5"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-sm font-semibold w-5 text-center" data-testid={`cart-qty-${l.variant_id}`}>{l.qty}</span>
                        <button data-testid={`cart-qty-plus-${l.variant_id}`} onClick={() => updateQty(l.variant_id, l.qty + 1)} aria-label={`Increase quantity of ${l.name}`} className="p-1.5"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="text-sm font-bold">{formatINR(l.price * l.qty)}</span>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${l.variant_id}`} onClick={() => removeFromCart(l.variant_id)} aria-label={`Remove ${l.name} from bag`} className="self-start p-1 text-subtext hover:text-coral"><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>

            <div className="border-t border-line px-5 py-4 space-y-3 safe-bottom">
              <div className="flex gap-2">
                <input data-testid="cart-drawer-coupon-input" placeholder="Coupon code (try WELCOME10)" value={coupon?.code || ""}
                  onChange={(e) => setCoupon({ code: e.target.value.toUpperCase() })}
                  className="field flex-1 min-w-0 h-11 text-sm" aria-label="Coupon code" />
                <span className="sr-only">Applied at checkout</span>
              </div>
              <div className="flex justify-between text-sm"><span className="text-charcoal/70">Subtotal</span><span className="font-bold" data-testid="cart-drawer-subtotal">{formatINR(subtotal)}</span></div>
              <p className="text-xs text-charcoal/50">Shipping and {settings?.tax_wording?.toLowerCase() || "tax"} calculated at checkout.</p>
              <MagicCheckoutButton compact onBeforeOpen={() => setCartOpen(false)} />
              <Link to="/checkout" data-testid="cart-drawer-checkout-btn" onClick={() => setCartOpen(false)}
                className="btn-primary w-full">
                Checkout · {formatINR(subtotal)}
              </Link>
              <Link to="/cart" data-testid="cart-drawer-view-bag" onClick={() => setCartOpen(false)} className="block text-center text-sm font-semibold underline underline-offset-4">View bag</Link>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
